import { Dayjs } from 'dayjs';

// ==================== Core Task Types ====================

export interface GanttTask {
  id: number;
  key: string;
  name: string;
  startDate: string;
  endDate: string;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  duration: number;
  progress?: number;
  critical?: boolean;
  expanded?: boolean;
  children?: GanttTask[];
  level: TaskLevel;
  parentId?: number;
  baselines?: TaskBaseline[];
  status?: string;
  owner?: string;
}

export type TaskLevel = 'phase' | 'activity' | 'task';

export interface FlattenedTask extends GanttTask {
  rowIndex: number;
  indentLevel: number;
  isVisible: boolean;
}

// ==================== Dependency Types ====================

export interface GanttDependency {
  id?: number;
  from: number;
  to: number;
  type: DependencyType;
  lagDays?: number;
}

export type DependencyType = 'FS' | 'FF' | 'SS' | 'SF';

export const DependencyTypeMap: Record<number, DependencyType> = {
  1: 'SS',
  2: 'FS',
  3: 'FF',
  4: 'SF',
};

export const DependencyTypeLabels: Record<DependencyType, string> = {
  FS: 'Finish to Start',
  FF: 'Finish to Finish',
  SS: 'Start to Start',
  SF: 'Start to Finish',
};

// ==================== Baseline Types ====================

export interface TaskBaseline {
  baselineId: number;
  baselineName: string;
  startDate: string;
  endDate: string;
  duration: number;
}

export interface BaselineConfig {
  id: number;
  name: string;
  color: string;
  visible: boolean;
}

export const BaselineColors = [
  '#faad14', // Gold
  '#722ed1', // Purple
  '#13c2c2', // Cyan
  '#eb2f96', // Magenta
  '#52c41a', // Green
];

// ==================== Timeline Configuration ====================

export type ZoomLevel = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface TimelineConfig {
  zoomLevel: ZoomLevel;
  startDate: Dayjs;
  endDate: Dayjs;
  totalDays: number;
  dayWidth: number;
  headerHeight: number;
  rowHeight: number;
}

/**
 * `dayWidth` is chosen so the *selected* unit lands on a readable column, the
 * way P6 sizes its timescale — a week column ends up ~56px, a month ~91px, a
 * quarter ~118px, a year ~146px. Everything downstream still measures in days,
 * so fractional widths are expected at the coarse levels.
 */
export const ZoomPresets: Record<ZoomLevel, { dayWidth: number; label: string }> = {
  day: { dayWidth: 36, label: 'Day' },
  week: { dayWidth: 8, label: 'Week' },
  month: { dayWidth: 3, label: 'Month' },
  quarter: { dayWidth: 1.3, label: 'Quarter' },
  year: { dayWidth: 0.4, label: 'Year' },
};

/** Order used by the zoom in/out buttons — finest first. */
export const ZoomLevelOrder: ZoomLevel[] = ['day', 'week', 'month', 'quarter', 'year'];

/** Below this the weekend bands turn into unreadable noise, so we drop them. */
export const WEEKEND_SHADING_MIN_DAY_WIDTH = 6;

/** Keeps a short task visible as a sliver once zoomed out past a few px/day. */
export const MIN_BAR_WIDTH = 3;

export const ROW_HEIGHT = 40;
export const HEADER_HEIGHT = 60;
export const BAR_HEIGHT = 24;
export const BAR_PADDING = 8;
export const BASELINE_HEIGHT = 8;
export const BASELINE_OFFSET = 4;

// ==================== Fullscreen Stacking ====================

/**
 * Fullscreen has to clear the app chrome: the header (`.nk-header`) sits at
 * 1010 and drawers/modals at 1020, so anything below that gets clipped under
 * the top nav. `popupBase` is fed to a ConfigProvider as `zIndexPopupBase` so
 * every antd popup raised from inside the gantt — bar tooltips especially —
 * lands above the fullscreen surface rather than behind it.
 */
export const FULLSCREEN_Z = {
  backdrop: 1300,
  content: 1301,
  popupBase: 1400, // antd adds its own offsets: popover +30, tooltip +70
  modalMask: 1450,
  modal: 1500,
} as const;

// ==================== Visual/Rendering Types ====================

export interface TaskBarPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DependencyPathData {
  path: string;
  arrowPoints: string;
  midPoint: { x: number; y: number };
}

// ==================== Component Props ====================

export type GroupByType = 'phases' | 'taskList';

export interface CustomGanttChartProps {
  projectId: number;
  groupBy: GroupByType | string;
  onTaskClick?: (task: GanttTask) => void;
}

export interface GanttContextValue {
  tasks: FlattenedTask[];
  /**
   * The hierarchical task tree, before flattening. Exposed so consumers can
   * resolve labels against a node's true siblings — the flattened list only
   * carries expanded rows, which would make sibling-dependent formatting shift
   * as the user expands and collapses.
   */
  taskTree: GanttTask[];
  dependencies: GanttDependency[];
  timelineConfig: TimelineConfig;
  showCriticalPath: boolean;
  showBaselines: boolean;
  selectedBaselines: number[];
  baselines: BaselineConfig[];
  expandedKeys: Set<string>;
  criticalTaskIds: Set<number>;
  loading: boolean;
  baselineLoading: boolean;
  isFullscreen: boolean;
  projectId: number | null;
  toggleExpand: (key: string) => void;
  setZoomLevel: (level: ZoomLevel) => void;
  setShowCriticalPath: (show: boolean) => void;
  setShowBaselines: (show: boolean) => void;
  setSelectedBaselines: (ids: number[]) => void;
  setIsFullscreen: (fullscreen: boolean) => void;
  refreshData: () => void;
}

// ==================== API Response Types ====================

export interface GanttApiResponse {
  taskStore: {
    data: ApiTask[];
  };
  dependencyStore: {
    data: ApiDependency[];
  };
  projectStartDate: string | null;
  projectEndDate: string | null;
}

export interface ApiTask {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  duration?: number;
  expanded?: boolean;
  manuallyScheduled?: boolean;
  cls?: string;
  children?: ApiTask[];
  baselines?: TaskBaseline[];
  critical?: boolean;
  // The backend emits `progress` (format-response-to-gantt-tree.util.ts). This
  // was declared as `percentDone`, which is never present in the payload, so
  // every bar rendered 0% regardless of the real figure. `percentDone` is kept
  // as an accepted alias so an older payload still works.
  progress?: number;
  percentDone?: number;
  status?: string;
}

export interface ApiDependency {
  from: number;
  to: number;
  dependencyTypeId: number;
  lagDays?: number;
  type?: number;
  lag?: number;
}

export interface BaselineApiResponse {
  baselineId: number;
  baselineName: string;
  projectId: number;
  createdAt?: string;
}

// ==================== Task Colors ====================

export const TaskLevelColors: Record<TaskLevel, string> = {
  phase: '#4a90e2',
  activity: '#52c41a',
  task: '#1890ff',
};

export const CriticalPathColor = '#ff4d4f';
